import {createSelector} from '@reduxjs/toolkit'
import {QueryStatus} from '@reduxjs/toolkit/dist/query/react'
import getRoomConfig from 'components/data/URLParams/mappers/getRoomConfig'
import {getUserCountryWithFallback} from 'modules/meta/selectors'
import {RootState} from 'store'
import {urlParams} from 'utils/urlParams'

import {getPersistedSapiLabel} from '@vio/core/src/_web/sapiLabel/business'
import {
  selectIsAuthenticated,
  selectIsLoading,
  selectIsMemberPlus
} from '@vio/core/src/auth/modules/selectors'
import {getTaxDisplayLogic} from '@vio/core/src/localization/business/countries'
import {SearchOffer} from '@vio/core/src/offer/types/SearchOffer'
import {roomsToConfigurationObject} from '@vio/core/src/room/business/roomConfiguration'
import {RoomConfigurationType} from '@vio/core/src/room/types/RoomConfiguration'
import {GetOffersParams, searchApi} from '@vio/core/src/sapi/services/searchApi'
import {
  getPriceSortConfigFromTaxConfig,
  getUserTierForSapi
} from '@vio/core/src/sapi/utils'

import {filterOutVioOffers} from './utils'

const getStateParam = (state: RootState) => state
const getOfferIdParam = (_: RootState, offerId: string) => offerId

export const getAlternativeOffersQueryParams = createSelector(
  [
    selectIsAuthenticated,
    selectIsMemberPlus,
    selectIsLoading,
    getUserCountryWithFallback
  ],
  (
    isAuthenticated,
    isMemberPlus,
    isAuthLoading,
    userCountry
  ): GetOffersParams => {
    const params = urlParams()

    const {checkIn, checkOut, rooms, hotelId, vclid} = params
    const taxDisplayLogic = getTaxDisplayLogic(userCountry)
    const {numberOfRooms} = getRoomConfig({rooms})
    const label = getPersistedSapiLabel()
    const tier = getUserTierForSapi({
      isAuthenticated,
      isMemberPlus,
      urlParams: params
    })

    const metadata = {
      ...(vclid ? {vclid} : {})
    }

    return {
      checkIn,
      checkOut,
      hotelId,
      rooms,
      label,
      getAllOffers: true,
      includeLocalTaxes: taxDisplayLogic.includeLocalTaxes,
      includeTaxes: taxDisplayLogic.includeTaxes,
      offersSort: getPriceSortConfigFromTaxConfig(taxDisplayLogic),
      numberOfRooms,
      metadata,
      shouldDispatchActions: true,
      ...(isAuthLoading ? {} : {tier})
    }
  }
)

const getRedirectOffersSelector = createSelector(
  [getAlternativeOffersQueryParams],
  (alternativeOffersQueryParams: GetOffersParams) =>
    searchApi.endpoints.getOffers.select(alternativeOffersQueryParams)
)

export const getOffersQuery = createSelector(
  [getStateParam, getRedirectOffersSelector],
  (state, redirectOffers) => redirectOffers(state)
)

const getOffersQueryData = createSelector(
  [getOffersQuery],
  ({status, data}) => {
    if (status === QueryStatus.fulfilled) {
      return data
    }
  }
)

const getOffersEntity = createSelector(
  [getOffersQueryData],
  data => data?.offerEntity
)

/**
 * Gets SAPI.offers endpoint request state (not rtkq query status)
 * @returns (SAPI)OffersState
 */
export const getAlternativeOffersLoadingState = createSelector(
  [getOffersQueryData],
  data => data?.status
)

/**
 * Gets filtered (only non-bofh), first 3 offers
 */
export const getAlternativeOffersList = createSelector(
  [getOffersEntity],
  offersEntity => filterOutVioOffers(offersEntity?.offers || []).slice(0, 3)
)

/**
 * Gets offer by id
 */
export const getRedirectOfferById = createSelector(
  [getOfferIdParam, getAlternativeOffersList],
  (offerId, offers): SearchOffer | null =>
    offers?.find(offer => offer.id === offerId) || null
)

/**
 * Gets rooms config from query params
 */
export const getRoomsConfigFromQueryParams = createSelector(
  [getAlternativeOffersQueryParams],
  ({rooms}): RoomConfigurationType => roomsToConfigurationObject(rooms)
)
